import { useCallback, useMemo, memo } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Fade } from 'react-awesome-reveal';
import clsx from 'clsx';

// Imports => Constants
import {
  DATETIME_FORMATS,
  KEYS,
  SIZES,
  THEMES,
} from '@opp/ui-kit';

// Imports => Utilities
import {
  AcIsSet,
  AcCapitalize,
} from '@opp/ui-kit';
import { AcFormatDate } from '@utils';

// Imports => Atoms
import {
  AcCard,
  AcHeading,
  AcBadge,
  AcRichContent,
  AcRipple,
} from '@opp/ui-kit';
import {withTranslation} from "react-i18next";

const _CLASSES = {
  MAIN: 'ac-ticket-list-item',
  INSET_BADGE: 'ac-ticket-list-item__inset-badge',
  LINK: 'ac-ticket-list-item__link',
  BODY: 'ac-ticket-list-item__body',
  FOOTER: 'ac-ticket-list-item__footer',
  TITLE: 'ac-ticket-list-item__title',
  STATUS: {
    AWAITING: 'ac-ticket-list-item--awaiting',
    REPLIED: 'ac-ticket-list-item--replied',
    OPEN: 'ac-ticket-list-item--open',
    ONHOLD: 'ac-ticket-list-item--onhold',
    CLOSED: 'ac-ticket-list-item--closed',
  },
  DESCRIPTION: 'ac-ticket-list-item__description',
  DATETIME: 'ac-ticket-list-item__datetime',
};

const AcTicketListItem = ({ data = {}, className, i18n , t}) => {
  const getTitleClassNames = useMemo(() => {
    return clsx(_CLASSES.TITLE);
  }, []);

  const getDescriptionClassNames = useMemo(() => {
    return clsx(_CLASSES.DESCRIPTION);
  }, []);

  const getDatetimeClassNames = useMemo(() => {
    return clsx(_CLASSES.DATETIME);
  }, []);

  const getFooterClassNames = useMemo(() => {
    return clsx(_CLASSES.FOOTER);
  }, []);

  const getLinkClassNames = useMemo(() => {
    return clsx(_CLASSES.LINK);
  }, []);

  const getBodyClassNames = useMemo(() => {
    return clsx(_CLASSES.BODY);
  }, []);

  const getMainClassNames = useMemo(() => {
    let { status } = data;

    if (status === 'onhold' || status === 'open') {
      status = 'awaiting';
    }

    return clsx(
      _CLASSES.MAIN,
      status && _CLASSES.STATUS[status.toUpperCase()],
      className
    );
  }, [data, className]);

  const getInsetBadgeClassName = useMemo(() => {
    return clsx(
      _CLASSES.INSET_BADGE,
    );
  }, []);

  const getID = useMemo(() => {
    return data && data.uid;
  }, [data]);

  const getCreatedDateFormatted = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.updated)) return null;

    const updated = data.updated * 1000;
    const result = AcFormatDate(
      updated,
      null,
      DATETIME_FORMATS.FULL_DAY_WITH_YEAR_AND_TIME
    );

    return <AcRichContent className={getDatetimeClassNames} content={result} />;
  }, [data, i18n.resolvedLanguage]);

  const getCategoryFormatted = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.category)) return null;

    return (
      <AcRichContent
        className={getDescriptionClassNames}
        content={`<p>${t(`category.${data.category}`, {ns: 'support'})}</p>`}
      />
    );
  }, [data]);

  const getStatusFormatted = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.status)) return null;

    let status = data.status;

    if (status === 'onhold' || status === 'open') {
      status = 'awaiting';
    }

    let message = t(`status.${status}`, {ns: 'common'});

    return <AcBadge>{message}</AcBadge>;
  }, [data]);

  const getSubjectFormatted = useMemo(() => {
    return (
      <AcHeading rank={5} className={getTitleClassNames}>
        {data.subject}
      </AcHeading>
    );
  }, [data]);

  const getTicketDetailRoute = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.url)) return null;

    return data.url;
  }, [data]);

  return (
    <Fade direction={'right'} duration={300} triggerOnce>
      <AcCard className={getMainClassNames} id={getID}>
        <a
          href={getTicketDetailRoute}
          className={getLinkClassNames}
          target={'_blank'}
          title={t('open-ticket-new-window', 'Open ticket {{id}} in a new window', {id: `${getID}`, ns: 'support'})} rel="noreferrer"
        >
          <AcRipple theme={THEMES.SUBTLE} size={SIZES.SMALL} simple />
          <div className={getBodyClassNames}>
            {getStatusFormatted}
            {getSubjectFormatted}
            {getCategoryFormatted}
          </div>
          {data.status && data.status === 'replied' &&
                        <span className={getInsetBadgeClassName} />
          }
          <div className={getFooterClassNames}>{getCreatedDateFormatted}</div>
        </a>
      </AcCard>
    </Fade>
  );
};

AcTicketListItem.propTypes = {
  /**
     * For CSS customization
     */
  className: PropTypes.string,
};

export default withTranslation(['support', 'common'])(memo(AcTicketListItem));
